/**
 * O bloco técnico de anexos que o backend anexa ao FINAL do `content` do
 * usuário (spec-016 [T023-back], `aawz_intelligence_v2/core/anexos_do_turno.py::
 * com_bloco_de_anexos`) — e como separá-lo do texto para a bolha.
 *
 * ★ **Por que o bloco existe no `content` persistido.** O bloco entra DENTRO
 * da fala do usuário (não num campo próprio) porque o modelo precisa do
 * `upload_id` nos turnos seguintes ao reler o histórico — é a mesma regra que
 * `docs/arquitetura-chat.md` §8 documenta para `[contexto da tela]`. O que
 * muda é a POSIÇÃO: o contexto de tela abre a mensagem (`falaDoConsultor`,
 * `@modules/fee-fixo/api/apiFeeFixoIa`, só corta prefixo); o bloco de anexos
 * fecha a mensagem — por isso este util é DIFERENTE do `falaDoConsultor`, não
 * uma extensão dele.
 *
 * ★ Ao vivo a bolha usa `message.anexos` (eco
 * local, `File[]` em memória — ver `chat-message.vue::anexosDaMensagem`); esse
 * campo nunca sobrevive ao F5 (`GET /messages` não devolve `anexos`), e o que
 * sobra é o `content` cru com o bloco técnico — a bolha mostrava
 * "…[Anexos desta mensagem — use o upload_id…] - arquivo.xlsx (mime, N
 * bytes): upload_id=<uuid>" na tela do consultor. Este util devolve
 * `{texto, anexos}` para a bolha desenhar o texto limpo + chips "📎 nome".
 *
 * ★ **Cabeçalho e formato de linha são BYTE A BYTE os do backend** — redigitar
 * qualquer um deles aqui faria o strip parar de casar em silêncio no dia em
 * que o texto mudasse lá. Teste de junção:
 * `anexos-da-mensagem.contrato.unit.js`, que lê o `.py` do backend quando ele
 * existir e FALHA (nunca `it.skip`) quando não encontrar o checkout irmão.
 *
 * **Falha segura** (mesmo princípio de `falaDoConsultor`): bloco no MEIO do
 * texto, ou linha que não bate o formato exato, devolvem o texto INTEIRO sem
 * anexo nenhum — cortar errado é pior do que não cortar.
 */

/** Byte a byte o `PREFIXO_DO_BLOCO` de `core/anexos_do_turno.py` (backend). */
export const PREFIXO_DO_BLOCO_DE_ANEXOS =
  '[Anexos desta mensagem — use o upload_id ao chamar ferramentas de ' +
  'importação/leitura]'

// `com_bloco_de_anexos` (backend) junta com `f'{texto}\n\n{bloco}'` — o MESMO
// separador que `MARCA_DO_CONTEXTO`/`falaDoConsultor` usa do lado do prefixo.
const SEPARADOR = '\n\n'

// `bloco_anexos_do_turno` (backend): `f'- {nome} ({content_type}, {tamanho}
// bytes): upload_id={upload_id}'`. O nome do arquivo pode conter parênteses —
// por isso o grupo do `content_type`/`tamanho` casa do FIM da linha pra trás
// (non-greedy no nome), não pelo primeiro `(` que aparecer.
const REGEX_LINHA_DE_ANEXO = /^- (.+) \(([^()]+), (\d+) bytes\): upload_id=(.+)$/

/**
 * Tenta parsear cada linha como um item do bloco. `null` na primeira linha que
 * não bater — é o gatilho da falha segura.
 */
function parsearLinhasDeAnexo(linhas) {
  const anexos = []
  for (let i = 0; i < linhas.length; i += 1) {
    const match = REGEX_LINHA_DE_ANEXO.exec(linhas[i])
    if (!match) return null
    anexos.push({
      nome: match[1],
      content_type: match[2],
      tamanho: Number(match[3]),
      upload_id: match[4],
    })
  }
  return anexos
}

/**
 * Separa o TEXTO do bloco técnico de anexos, quando ele existir ao FINAL da
 * string — nunca no meio (falha segura).
 *
 * @param {*} texto `content`/`display_message` da mensagem, como veio do banco
 *   (ou ao vivo, antes de qualquer bloco existir).
 * @returns {{texto: string, anexos: Array<{nome: string, content_type: string,
 *   tamanho: number, upload_id: string}>}}
 */
export function anexosDaMensagem(texto) {
  const semAnexo = { texto: typeof texto === 'string' ? texto : '', anexos: [] }
  if (typeof texto !== 'string' || !texto) return semAnexo

  // `com_bloco_de_anexos` devolve o bloco PURO quando não havia texto antes
  // (`bloco if not texto`) — sem separador para procurar.
  if (texto.indexOf(PREFIXO_DO_BLOCO_DE_ANEXOS) === 0) {
    const anexos = parsearLinhasDeAnexo(texto.split('\n').slice(1))
    if (!anexos || !anexos.length) return semAnexo
    return { texto: '', anexos: anexos }
  }

  const marcador = SEPARADOR + PREFIXO_DO_BLOCO_DE_ANEXOS
  const indice = texto.lastIndexOf(marcador)
  if (indice === -1) return semAnexo

  // Não checo "indice + marcador.length === texto.length": o marcador termina
  // no FIM do prefixo, mas o bloco de verdade continua com as linhas de item
  // depois dele. Quem garante "não está no meio" é o PARSE abaixo — se o
  // usuário citou o texto do prefixo no meio de uma frase (ex.: entre aspas)
  // e o que vem depois não é uma linha de anexo válida, o parse falha e cai
  // na falha segura (texto intacto).
  const corpo = texto.slice(indice + SEPARADOR.length)
  const anexos = parsearLinhasDeAnexo(corpo.split('\n').slice(1))
  if (!anexos || !anexos.length) return semAnexo

  return { texto: texto.slice(0, indice), anexos: anexos }
}
